import { forwardRef } from 'react';
import clsx from 'clsx';
import type { BaziChart, Pillar } from '@guanwei/shared';
import { BRAND } from '@guanwei/shared';

interface BaziChartCardProps {
  bazi: BaziChart;
  /** 是否展示水印（用于分享导出图） */
  showWatermark?: boolean;
  className?: string;
  /** 用户昵称（导出时展示） */
  ownerName?: string;
}

const PILLAR_LABELS: Record<'year' | 'month' | 'day' | 'hour', string> = {
  year: '年柱',
  month: '月柱',
  day: '日柱',
  hour: '时柱',
};

type PillarPos = keyof typeof PILLAR_LABELS;

/**
 * 黑金质感八字命盘卡片
 *
 * 设计参考方案 §1.4：黑金极简，用 ref/system 主题令牌驱动
 * 暗色主题下尤为契合"暗夜星河"的品牌调性
 */
export const BaziChartCard = forwardRef<HTMLDivElement, BaziChartCardProps>(function BaziChartCard(
  { bazi, showWatermark, className, ownerName },
  ref,
) {
  const pillars: Array<{ pos: PillarPos; pillar: Pillar | null }> = [
    { pos: 'year', pillar: bazi.pillars.year },
    { pos: 'month', pillar: bazi.pillars.month },
    { pos: 'day', pillar: bazi.pillars.day },
    { pos: 'hour', pillar: bazi.pillars.hour },
  ];
  const shenShaByPillar = pillars.reduce<Record<PillarPos, string[]>>(
    (acc, { pos }) => {
      acc[pos] = bazi.shenSha.filter((item) => item.pillars.includes(pos)).map((item) => item.name);
      return acc;
    },
    { year: [], month: [], day: [], hour: [] },
  );

  return (
    <div
      ref={ref}
      className={clsx(
        'relative overflow-hidden rounded-xl bg-bg-1 border border-border-accent shadow-gold',
        'p-4 sm:p-8',
        className,
      )}
    >
      {/* 顶部品牌带 */}
      <div className="mb-6 flex flex-col gap-4 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="font-serif text-3xl text-accent">觀</span>
          <div>
            <p className="font-serif text-base text-fg leading-tight">{BRAND.nameZh} · 八字排盘</p>
            <p className="text-[10px] tracking-[0.3em] text-fg-muted uppercase">{BRAND.nameEn}</p>
          </div>
        </div>
        <div className="text-right text-xs text-fg-muted">
          {ownerName && <p className="text-fg-2">{ownerName}</p>}
          <p>
            {bazi.effectiveDate}
            {bazi.effectiveTime ? ` ${bazi.effectiveTime}` : ' 时辰未知'}
          </p>
          {bazi.trueSolarOffsetMinutes !== 0 && (
            <p className="text-[10px] text-fg-muted">
              真太阳时校正 {bazi.trueSolarOffsetMinutes > 0 ? '+' : ''}
              {bazi.trueSolarOffsetMinutes.toFixed(0)} 分
            </p>
          )}
          {bazi.ziDayShift === 1 && <p className="text-[10px] text-fg-muted">23点换日柱</p>}
        </div>
      </div>

      {/* 四柱网格 */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {pillars.map(({ pos, pillar }) => (
          <PillarBlock
            key={pos}
            label={PILLAR_LABELS[pos]}
            pillar={pillar}
            isDay={pos === 'day'}
            fuzzyHour={pos === 'hour' && pillar === null}
            marker={pos === 'hour' ? bazi.ziLabel : null}
            shenSha={shenShaByPillar[pos]}
          />
        ))}
      </div>

      {bazi.extra && (
        <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-4 sm:grid-cols-4">
          <ExtraItem label="胎元" value={bazi.extra.taiYuan} />
          <ExtraItem label="命宫" value={bazi.extra.mingGong} />
          <ExtraItem label="身宫" value={bazi.extra.shenGong} />
          <ExtraItem label="旬空" value={bazi.extra.xunKong} />
        </div>
      )}

      {/* 分柱神煞 */}
      {bazi.shenSha.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <p className="text-xs text-fg-muted">神煞（按柱）</p>
            <p className="text-[10px] text-fg-muted">年 / 月 / 日 / 时分柱展示</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {pillars.map(({ pos }) => (
              <div key={pos} className="rounded-md border border-border bg-bg-2 px-3 py-2">
                <p className="mb-1 text-[10px] text-fg-muted">{PILLAR_LABELS[pos]}</p>
                {shenShaByPillar[pos].length > 0 ? (
                  <p className="text-xs leading-5 text-accent">{shenShaByPillar[pos].join(' ')}</p>
                ) : (
                  <p className="text-xs text-fg-muted">—</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 大运预览 */}
      {bazi.daYun.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <p className="text-xs text-fg-muted">大运（前 5 步）</p>
            {bazi.yunTiming && (
              <p className="text-[10px] text-fg-muted">
                {bazi.yunTiming.direction === 'forward' ? '顺行' : '逆行'} · 起运约{' '}
                {bazi.yunTiming.years}年{bazi.yunTiming.months}月{bazi.yunTiming.days}日
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {bazi.daYun.slice(0, 5).map((d) => (
              <div key={d.startYear} className="text-center rounded-md bg-bg-2 py-2 px-1">
                <p className="font-serif text-base text-accent">{d.ganZhi}</p>
                <p className="text-[10px] text-fg-2 mt-0.5">{d.shiShen}</p>
                <p className="text-[10px] text-fg-muted mt-0.5">
                  {d.startAge}岁 / {d.startYear}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 底部信息 */}
      <div className="mt-6 flex flex-col gap-2 border-t border-border pt-4 text-[10px] text-fg-muted sm:flex-row sm:items-center sm:justify-between">
        <span>
          日主 <span className="text-accent font-serif text-sm">{bazi.dayMaster}</span>
          {' · '}
          农历 {bazi.lunar.year} 年{bazi.lunar.leapMonth ? '闰' : ''}
          {bazi.lunar.month}月{bazi.lunar.day}日
        </span>
        <span className="font-serif tracking-widest">{BRAND.tagline}</span>
      </div>

      {/* 水印 —— 仅在分享导出时启用 */}
      {showWatermark && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span className="font-serif text-[120px] text-accent opacity-[0.04] rotate-[-25deg] select-none">
            觀微
          </span>
        </div>
      )}
    </div>
  );
});

function ExtraItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-bg-2 px-3 py-2 text-center">
      <p className="text-[10px] text-fg-muted">{label}</p>
      <p className="mt-1 font-serif text-sm text-fg">{value || '—'}</p>
    </div>
  );
}

function PillarBlock({
  label,
  pillar,
  isDay,
  fuzzyHour,
  marker,
  shenSha,
}: {
  label: string;
  pillar: Pillar | null;
  isDay: boolean;
  fuzzyHour: boolean;
  marker?: string | null;
  shenSha: string[];
}) {
  if (fuzzyHour) {
    return (
      <div className="text-center rounded-lg border border-dashed border-border bg-bg-2 py-4 px-2">
        <p className="text-xs text-fg-muted mb-3">{label}</p>
        <p className="font-serif text-2xl text-fg-muted">？</p>
        <p className="text-[10px] text-fg-muted mt-2">时辰未知</p>
      </div>
    );
  }
  if (!pillar) return null;

  return (
    <div
      className={clsx(
        'text-center rounded-lg py-4 px-2 border',
        isDay ? 'bg-accent-soft border-border-accent' : 'bg-bg-2 border-border',
      )}
    >
      <p className={clsx('text-xs mb-3', isDay ? 'text-accent font-medium' : 'text-fg-muted')}>
        {label}
        {isDay && <span className="ml-1">（日主）</span>}
      </p>

      {/* 干支主字 */}
      <p className="font-serif text-2xl leading-none text-fg sm:text-3xl">
        <span className={isDay ? 'text-accent' : ''}>{pillar.stem}</span>
      </p>
      <p className="mt-1 font-serif text-2xl leading-none text-fg sm:text-3xl">{pillar.branch}</p>

      {/* 十神 */}
      <p className="text-[10px] text-fg-2 mt-3 min-h-[14px]">{pillar.shiShen ?? ''}</p>
      {marker && <p className="mt-1 text-[10px] text-accent">[{marker}]</p>}

      {/* 纳音 / 长生 */}
      <p className="text-[10px] text-fg-muted mt-1">{pillar.naYin}</p>
      {pillar.changSheng && (
        <p className="text-[10px] text-fg-muted mt-1">长生：{pillar.changSheng}</p>
      )}

      {/* 藏干 */}
      <div className="mt-2 flex flex-wrap justify-center gap-1">
        {pillar.hiddenStems.map((h, i) => (
          <span key={i} className="text-[9px] text-fg-muted">
            {h.stem}
            <span className="ml-0.5 text-fg-muted/70">{h.shiShen}</span>
          </span>
        ))}
      </div>

      {shenSha.length > 0 && (
        <p className="mt-2 border-t border-border pt-2 text-[10px] leading-4 text-accent">
          {shenSha.join(' ')}
        </p>
      )}
    </div>
  );
}
